import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import './Ladders.css';
import { toast } from 'react-toastify';
import AssetDetailModal from '../shared/AssetDetailModal';
import { openLatestTestReport } from '../shared/openLatestTestReport';
import { isSuperAdmin } from '../../../../utils/roleUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye, faEdit, faTrash, faSearch, faFilter, faPlus, faXmark, faLadderWater, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

const LADDER_TYPES = [
  'Fixed Vertical Ladder',
  'Fixed Vertical Ladder with Fall Arrest',
  'Caged Ladder',
  'Hook-On Ladder',
  'Step Ladder',
  'Foldable Ladder',
  'Safety Ladder with Restraint Rail',
  'Angled Ladder',
  'Retractable Ladder',
  'Roof Access Ladder'
];

const LADDER_MATERIALS = [
  'Steel',
  'Aluminium',
  'GRP',
  'Fibreglass'
];

const getBadgeClass = (status) => {
  const key = (status || 'unknown').toLowerCase();
  const map = {
    pass: 'rs-badge-pass', passed: 'rs-badge-passed',
    fail: 'rs-badge-fail', failed: 'rs-badge-failed',
    pending: 'rs-badge-pending', overdue: 'rs-badge-overdue',
    unknown: 'rs-badge-unknown'
  };
  return `rs-badge ${map[key] || 'rs-badge-unknown'}`;
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try {
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleDateString('en-GB');
  } catch {
    return 'N/A';
  }
};

const EMPTY_LADDER = {
  ladderId: '',
  location: '',
  type: LADDER_TYPES[0],
  height: '',
  material: LADDER_MATERIALS[0],
  installedBy: '',
  dateInstalled: new Date().toISOString().split('T')[0],
  lastTestDate: new Date().toISOString().split('T')[0],
  status: 'Unknown',
  certificateUrl: '',
  notes: ''
};

export default function Ladders({ propertyId }) {
  const [ladders, setLadders] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLadder, setEditingLadder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newLadder, setNewLadder] = useState({ ...EMPTY_LADDER });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [detailAsset, setDetailAsset] = useState(null);

  const calculateNextTestDate = (lastTestDate) => {
    const date = new Date(lastTestDate);
    date.setMonth(date.getMonth() + 12);
    return date.toISOString().split('T')[0];
  };

  const fetchLaddersData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/ladders`), {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch ladders: ${response.status}`);

      const data = await response.json();
      const laddersArray = Array.isArray(data) ? data : [data];

      setLadders(laddersArray.map(ladder => ({
        id: ladder.id,
        propertyId: ladder.property_id,
        ladderId: ladder.ladder_id || `Ladder-${ladder.id}`,
        location: ladder.location_description || ladder.location,
        type: ladder.type || 'Fixed',
        height: ladder.height ? `${ladder.height}m` : 'N/A',
        material: ladder.material || 'N/A',
        installedBy: ladder.installed_by || 'N/A',
        dateInstalled: formatDate(ladder.date_installed),
        lastTestDate: formatDate(ladder.last_inspection_date),
        nextTestDate: ladder.next_inspection_date
          ? formatDate(ladder.next_inspection_date)
          : (ladder.last_inspection_date ? calculateNextTestDate(ladder.last_inspection_date) : 'N/A'),
        status: ladder.status || 'Unknown',
        certificateUrl: ladder.certificate_url,
        testReportUrl: ladder.test_report_url,
        notes: ladder.notes || ''
      })));
    } catch (err) {
      console.error('Error in fetchLaddersData:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to fetch ladders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLadder = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/ladders`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ladder_id: newLadder.ladderId,
          location_description: newLadder.location,
          type: newLadder.type,
          height: newLadder.height,
          material: newLadder.material,
          installed_by: newLadder.installedBy,
          date_installed: newLadder.dateInstalled,
          last_inspection_date: newLadder.lastTestDate,
          status: 'Unknown',
          certificate_url: newLadder.certificateUrl,
          notes: newLadder.notes
        })
      });

      if (!response.ok) throw new Error('Failed to create ladder');

      const createdLadder = await response.json();
      setLadders(prev => [...prev, {
        ...createdLadder,
        ladderId: createdLadder.ladder_id,
        location: createdLadder.location_description,
        installedBy: createdLadder.installed_by,
        dateInstalled: formatDate(createdLadder.date_installed),
        lastTestDate: formatDate(createdLadder.last_inspection_date),
        nextTestDate: calculateNextTestDate(createdLadder.last_inspection_date),
        certificateUrl: createdLadder.certificate_url,
        testReportUrl: createdLadder.test_report_url
      }]);
      setShowCreateModal(false);
      setNewLadder({ ...EMPTY_LADDER });
      toast.success('Ladder created successfully');
      fetchLaddersData();
    } catch (err) {
      console.error('Error creating ladder:', err);
      toast.error(err.message || 'Failed to create ladder');
    }
  };

  const handleEditLadder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/ladders/${editingLadder.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ladder_id: editingLadder.ladderId,
          location_description: editingLadder.location,
          type: editingLadder.type,
          height: editingLadder.height,
          material: editingLadder.material,
          installed_by: editingLadder.installedBy,
          date_installed: editingLadder.dateInstalled,
          last_inspection_date: editingLadder.lastTestDate,
          status: editingLadder.status,
          certificate_url: editingLadder.certificateUrl,
          notes: editingLadder.notes
        })
      });

      if (!response.ok) throw new Error('Failed to update ladder');

      const updatedLadder = await response.json();
      setLadders(ladders.map(ladder =>
        ladder.id === updatedLadder.id ? {
          ...updatedLadder,
          ladderId: updatedLadder.ladder_id,
          location: updatedLadder.location_description,
          installedBy: updatedLadder.installed_by,
          dateInstalled: formatDate(updatedLadder.date_installed),
          lastTestDate: formatDate(updatedLadder.last_inspection_date),
          nextTestDate: calculateNextTestDate(updatedLadder.last_inspection_date),
          certificateUrl: updatedLadder.certificate_url,
          testReportUrl: updatedLadder.test_report_url
        } : ladder
      ));
      setShowEditModal(false);
      setEditingLadder(null);
      toast.success('Ladder updated successfully');
    } catch (err) {
      console.error('Error updating ladder:', err);
      toast.error(err.message || 'Failed to update ladder');
    }
  };

  const handleDeleteLadder = async (ladderId) => {
    if (!window.confirm('Are you sure you want to delete this ladder?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}/ladders/${ladderId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete ladder');
      setLadders(ladders.filter(ladder => ladder.id !== ladderId));
      setShowEditModal(false);
      setEditingLadder(null);
      toast.success('Ladder deleted successfully');
    } catch (err) {
      console.error('Error deleting ladder:', err);
      toast.error(err.message || 'Failed to delete ladder');
    }
  };

  const handleViewTestData = (ladder) => {
    openLatestTestReport({
      propertyId,
      assetType: 'ladder',
      assetId: ladder.id,
      fallbackAsset: ladder,
      emptyMessage: 'No test data available for this ladder',
      onEmpty: (msg) => toast.info(msg),
    });
  };

  useEffect(() => {
    if (propertyId) fetchLaddersData();
  }, [propertyId]);

  // Auto-open asset from drawing hotspot navigation
  useEffect(() => {
    const handler = (e) => {
      const { assetId, assetTable } = e.detail || {};
      if (assetTable === 'ladders' && assetId && ladders.length) {
        const asset = ladders.find(a => a.id === parseInt(assetId));
        if (asset) setDetailAsset(asset);
      }
    };
    window.addEventListener('openAssetFromHotspot', handler);
    return () => window.removeEventListener('openAssetFromHotspot', handler);
  }, [ladders]);

  /* Close filter dropdown on outside click */
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('.rs-filter-dropdown') && !e.target.closest('.filter-toggle-btn')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterDropdown]);

  /* Filtered data */
  const filtered = ladders
    .filter(l => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return [l.ladderId, l.location, l.type, l.status, l.material].some(v => v && v.toLowerCase().includes(q));
    })
    .filter(l => typeFilter === 'All' || l.type === typeFilter);

  /* ─── Error State ─── */
  if (error) {
    return (
      <div className="ladders-section">
        <h1>Ladders</h1>
        <div className="rs-error-alert">
          <FontAwesomeIcon icon={faLadderWater} />
          <span>{error}</span>
          <button onClick={fetchLaddersData}>Retry</button>
        </div>
      </div>
    );
  }

  /* ─── Loading State ─── */
  if (loading) {
    return (
      <div className="ladders-section">
        <h1>Ladders</h1>
        <div className="rs-skeleton">
          {[...Array(6)].map((_, i) => <div key={i} className="rs-skeleton-row" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="ladders-section">
      <h1>Ladders</h1>

      {/* ─── Toolbar ─── */}
      <div className="rs-toolbar">
        <div className="rs-search-bar">
          <FontAwesomeIcon icon={faSearch} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Search ladders..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* ─── Table / Cards ─── */}
      <div className="rs-table-card">
        {/* Desktop Table */}
        <div className="rs-table-scroll">
          <table className="rs-table">
            <thead>
              <tr>
                <th>Ladder ID</th>
                <th>Location</th>
                <th>Type</th>
                <th>Height</th>
                <th>Material</th>
                <th>Installed By</th>
                <th>Date Installed</th>
                <th>Last Test</th>
                <th>Next Test</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="11">
                    <div className="rs-empty-state">
                      <div className="rs-empty-icon"><FontAwesomeIcon icon={faLadderWater} /></div>
                      <h3 className="rs-empty-title">
                        {ladders.length === 0 ? 'No ladders yet' : 'No results found'}
                      </h3>
                      <p className="rs-empty-desc">
                        {ladders.length === 0
                          ? 'Add your first ladder to start tracking safety compliance.'
                          : 'Try adjusting your search or filter criteria.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map(ladder => (
                <tr key={ladder.id}>
                  <td title={ladder.ladderId}>{ladder.ladderId}</td>
                  <td title={ladder.location}>{ladder.location}</td>
                  <td title={ladder.type}>{ladder.type}</td>
                  <td>{ladder.height}</td>
                  <td>{ladder.material}</td>
                  <td>{ladder.installedBy}</td>
                  <td>{ladder.dateInstalled}</td>
                  <td>{ladder.lastTestDate}</td>
                  <td>{ladder.nextTestDate}</td>
                  <td>
                    <span className={getBadgeClass(ladder.status)}>
                      {ladder.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="rs-actions">
                      <button className="rs-action-btn" onClick={() => handleViewTestData(ladder)} title="View Test Data" aria-label="View test data">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button className="rs-action-btn" onClick={() => setDetailAsset(ladder)} title="Test History" aria-label="View test history">
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </button>
                      {isSuperAdmin() && (<>
                      <button className="rs-action-btn" onClick={() => { setEditingLadder(ladder); setShowEditModal(true); }} title="Edit" aria-label="Edit ladder">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="rs-action-btn rs-action-delete" onClick={() => handleDeleteLadder(ladder.id)} title="Delete" aria-label="Delete ladder">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      </>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="rs-mobile-cards">
          {filtered.length === 0 ? (
            <div className="rs-empty-state">
              <div className="rs-empty-icon"><FontAwesomeIcon icon={faLadderWater} /></div>
              <h3 className="rs-empty-title">{ladders.length === 0 ? 'No ladders yet' : 'No results found'}</h3>
              <p className="rs-empty-desc">{ladders.length === 0 ? 'Add your first ladder.' : 'Adjust your filters.'}</p>
            </div>
          ) : filtered.map(ladder => (
            <div key={ladder.id} className="rs-mobile-card">
              <div className="rs-mobile-card-header">
                <span className="rs-mobile-card-title">{ladder.ladderId}</span>
                <span className={getBadgeClass(ladder.status)}>{ladder.status || 'Unknown'}</span>
              </div>
              <div className="rs-mobile-card-meta">
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Location</span><span className="rs-mobile-card-value">{ladder.location}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Type</span><span className="rs-mobile-card-value">{ladder.type}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Height</span><span className="rs-mobile-card-value">{ladder.height}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Material</span><span className="rs-mobile-card-value">{ladder.material}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Last Test</span><span className="rs-mobile-card-value">{ladder.lastTestDate}</span></div>
                <div className="rs-mobile-card-field"><span className="rs-mobile-card-label">Next Test</span><span className="rs-mobile-card-value">{ladder.nextTestDate}</span></div>
              </div>
              <div className="rs-mobile-card-actions">
                <button className="rs-btn rs-btn-secondary" onClick={() => handleViewTestData(ladder)}><FontAwesomeIcon icon={faEye} /> View</button>
                <button className="rs-btn rs-btn-secondary" onClick={() => setDetailAsset(ladder)}><FontAwesomeIcon icon={faClockRotateLeft} /> Test History</button>
                {isSuperAdmin() && (<>
                <button className="rs-btn rs-btn-secondary" onClick={() => { setEditingLadder(ladder); setShowEditModal(true); }}><FontAwesomeIcon icon={faEdit} /> Edit</button>
                <button className="rs-btn rs-btn-danger" onClick={() => handleDeleteLadder(ladder.id)}><FontAwesomeIcon icon={faTrash} /> Delete</button>
                </>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Create Modal ─── */}
      {showCreateModal && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Add new ladder">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Add New Ladder</h3>
              <button className="rs-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleCreateLadder(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>Ladder ID</label>
                    <input type="text" value={newLadder.ladderId} onChange={e => setNewLadder({ ...newLadder, ladderId: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input type="text" value={newLadder.location} onChange={e => setNewLadder({ ...newLadder, location: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select value={newLadder.type} onChange={e => setNewLadder({ ...newLadder, type: e.target.value })} required>
                      {LADDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Height</label>
                    <input type="text" value={newLadder.height} onChange={e => setNewLadder({ ...newLadder, height: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <select value={newLadder.material} onChange={e => setNewLadder({ ...newLadder, material: e.target.value })} required>
                      {LADDER_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input type="text" value={newLadder.installedBy} onChange={e => setNewLadder({ ...newLadder, installedBy: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input type="date" value={newLadder.dateInstalled} onChange={e => setNewLadder({ ...newLadder, dateInstalled: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input type="date" value={newLadder.lastTestDate} onChange={e => setNewLadder({ ...newLadder, lastTestDate: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Certificate URL</label>
                    <input type="text" value={newLadder.certificateUrl} onChange={e => setNewLadder({ ...newLadder, certificateUrl: e.target.value })} />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea value={newLadder.notes} onChange={e => setNewLadder({ ...newLadder, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Create Ladder</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Edit Modal ─── */}
      {showEditModal && editingLadder && (
        <div className="rs-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit ladder">
          <div className="rs-modal">
            <div className="rs-modal-header">
              <h3>Edit Ladder</h3>
              <button className="rs-modal-close" onClick={() => { setShowEditModal(false); setEditingLadder(null); }} aria-label="Close modal">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); await handleEditLadder(); }}>
              <div className="rs-modal-body">
                <div className="rs-form-grid">
                  <div className="rs-form-group">
                    <label>Ladder ID</label>
                    <input type="text" value={editingLadder.ladderId || ''} onChange={e => setEditingLadder({ ...editingLadder, ladderId: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Location</label>
                    <input type="text" value={editingLadder.location || ''} onChange={e => setEditingLadder({ ...editingLadder, location: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Type</label>
                    <select value={editingLadder.type || ''} onChange={e => setEditingLadder({ ...editingLadder, type: e.target.value })} required>
                      {LADDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Height</label>
                    <input type="text" value={editingLadder.height || ''} onChange={e => setEditingLadder({ ...editingLadder, height: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Material</label>
                    <select value={editingLadder.material || ''} onChange={e => setEditingLadder({ ...editingLadder, material: e.target.value })} required>
                      {LADDER_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="rs-form-group">
                    <label>Installed By</label>
                    <input type="text" value={editingLadder.installedBy || ''} onChange={e => setEditingLadder({ ...editingLadder, installedBy: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Date Installed</label>
                    <input type="date" value={editingLadder.dateInstalled || ''} onChange={e => setEditingLadder({ ...editingLadder, dateInstalled: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Last Test Date</label>
                    <input type="date" value={editingLadder.lastTestDate || ''} onChange={e => setEditingLadder({ ...editingLadder, lastTestDate: e.target.value })} required />
                  </div>
                  <div className="rs-form-group">
                    <label>Certificate URL</label>
                    <input type="text" value={editingLadder.certificateUrl || ''} onChange={e => setEditingLadder({ ...editingLadder, certificateUrl: e.target.value })} />
                  </div>
                  <div className="rs-form-group rs-full-width">
                    <label>Notes</label>
                    <textarea value={editingLadder.notes || ''} onChange={e => setEditingLadder({ ...editingLadder, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="rs-modal-footer">
                <button type="button" className="rs-btn rs-btn-secondary" onClick={() => { setShowEditModal(false); setEditingLadder(null); }}>Cancel</button>
                <button type="submit" className="rs-btn rs-btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AssetDetailModal
        isOpen={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        asset={detailAsset ? { ...detailAsset, name: detailAsset.name || detailAsset.ladder_id, location: detailAsset.location } : null}
        assetType="ladder"
        propertyId={propertyId}
        assetMeta={detailAsset ? [
          { label: 'Type', value: detailAsset.type },
          { label: 'Height', value: detailAsset.height },
          { label: 'Material', value: detailAsset.material },
          { label: 'Installed By', value: detailAsset.installedBy }
        ] : []}
      />
    </div>
  );
}
